import {
  Box,
  Chip,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  audit,
  type AdminApiCallLogsQuery,
  type AuditEventQuery,
  type AuditTransactionQuery,
} from '@/api/platform';
import { DataTable } from '@/components/DataTable';
import { FieldRow } from '@/components/FieldRow';
import { Pagination } from '@/components/Pagination';
import { StageFlow } from '@/components/StageFlow';
import { StatusPill, auditStatusTone } from '@/components/StatusPill';
import { TenantSelect } from '@/components/TenantSelect';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, relativeTime, utcStamp } from '@/utils/format';
import { suggestResolution } from '@/utils/resolutions';
import type {
  AbdmApiCallLog,
  AbdmEventLog,
  AbdmTransactionLog,
  StageStatus,
  TraceTimeline,
} from '@/types/backend';

/**
 * Cross-tenant audit viewer (PR-4 admin endpoints). Two tabs:
 *   - Events       — pass/fail step trail
 *   - Transactions — outbound gateway calls
 *
 * Filters live in the URL search params so a Slack link to a specific
 * filtered view is shareable. Clicking any row with a traceId opens a
 * drawer that joins both collections under that traceId — same shape
 * as `TenantAuditQueryService.timeline`.
 */

type TabKey = 'apiCalls' | 'events' | 'transactions';
const PAGE_SIZE = 50;

export function AuditViewerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') ?? 'apiCalls') as TabKey;
  const [tracePicked, setTracePicked] = useState<{ traceId: string; tenantId?: string } | null>(null);

  const tenantId = searchParams.get('tenantId') ?? '';
  const milestone = searchParams.get('milestone') ?? '';
  const status = searchParams.get('status') ?? '';
  const eventType = searchParams.get('eventType') ?? '';
  const apiName = searchParams.get('apiName') ?? '';
  const abdmStatus = searchParams.get('abdmStatus') ?? '';
  const page = Number(searchParams.get('page') ?? '0');

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next, { replace: true });
  };

  return (
    <PageShell
      kicker="PLATFORM · AUDIT"
      title="Audit Viewer"
      subtitle="Cross-tenant pictorial API-call summary, per-step pass/fail trail, and outbound gateway calls. Click any row with a trace id to see the stitched timeline."
      width="full"
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
        alignItems={{ md: 'center' }}
      >
        <TenantSelect value={tenantId} onChange={(v) => updateParam('tenantId', v)} />
        {tab === 'apiCalls' && (
          <TextField
            size="small"
            label="ABDM stage"
            select
            value={abdmStatus}
            onChange={(e) => updateParam('abdmStatus', e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="OK">OK</MenuItem>
            <MenuItem value="ERROR">ERROR (upstream issue)</MenuItem>
            <MenuItem value="SKIPPED">SKIPPED</MenuItem>
            <MenuItem value="N/A">N/A</MenuItem>
          </TextField>
        )}
        {tab === 'events' && (
          <>
            <TextField
              size="small"
              label="Milestone"
              select
              value={milestone}
              onChange={(e) => updateParam('milestone', e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="M1">M1</MenuItem>
              <MenuItem value="M2">M2</MenuItem>
              <MenuItem value="M3">M3</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Status"
              select
              value={status}
              onChange={(e) => updateParam('status', e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="PASS">PASS</MenuItem>
              <MenuItem value="FAIL">FAIL</MenuItem>
              <MenuItem value="PARTIAL">PARTIAL</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Event type"
              value={eventType}
              onChange={(e) => updateParam('eventType', e.target.value)}
              placeholder="e.g. ABHA_CREATE"
              sx={{ minWidth: 180 }}
            />
          </>
        )}
        {tab === 'transactions' && (
          <>
            <TextField
              size="small"
              label="Status"
              select
              value={status}
              onChange={(e) => updateParam('status', e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="SUCCESS">SUCCESS</MenuItem>
              <MenuItem value="FAILURE">FAILURE</MenuItem>
              <MenuItem value="TIMEOUT">TIMEOUT</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="API name"
              value={apiName}
              onChange={(e) => updateParam('apiName', e.target.value)}
              placeholder='e.g. ABDM:/v3/auth/...'
              sx={{ minWidth: 240 }}
            />
          </>
        )}
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => updateParam('tab', v)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          minHeight: 36,
          '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 500 },
        }}
      >
        <Tab value="apiCalls" label="API Calls" />
        <Tab value="events" label="Events" />
        <Tab value="transactions" label="Transactions" />
      </Tabs>

      {tab === 'apiCalls' && (
        <ApiCallsTab
          query={{
            tenantId: tenantId || undefined,
            page,
            size: PAGE_SIZE,
            ...(abdmStatus ? { abdmStatus: abdmStatus as Exclude<StageStatus, ''> } : {}),
          }}
          onPage={(n) => updateParam('page', String(n))}
          onTraceOpen={(traceId, tId) => setTracePicked({ traceId, tenantId: tId })}
        />
      )}
      {tab === 'events' && (
        <EventsTab
          query={{
            tenantId: tenantId || undefined,
            milestone: milestone || undefined,
            status: status || undefined,
            eventType: eventType || undefined,
            page,
            size: PAGE_SIZE,
          }}
          onPage={(n) => updateParam('page', String(n))}
          onTraceOpen={(traceId, tId) => setTracePicked({ traceId, tenantId: tId })}
        />
      )}
      {tab === 'transactions' && (
        <TransactionsTab
          query={{
            tenantId: tenantId || undefined,
            status: status || undefined,
            apiName: apiName || undefined,
            page,
            size: PAGE_SIZE,
          }}
          onPage={(n) => updateParam('page', String(n))}
          onTraceOpen={(traceId, tId) => setTracePicked({ traceId, tenantId: tId })}
        />
      )}

      <TraceDrawer pick={tracePicked} onClose={() => setTracePicked(null)} />
    </PageShell>
  );
}

function ApiCallsTab({
  query,
  onPage,
  onTraceOpen,
}: {
  query: AdminApiCallLogsQuery;
  onPage: (n: number) => void;
  onTraceOpen: (traceId: string, tenantId?: string) => void;
}) {
  const theme = useTheme();
  const swrKey = useMemo(() => ['admin-api-call-logs', query] as const, [query]);
  const { data, error, isLoading } = useSWR(swrKey, () => audit.apiCallLogs(query));

  return (
    <>
      <DataTable<AbdmApiCallLog>
        rows={data?.content}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        empty={{
          icon: SearchIcon,
          title: 'No API calls yet.',
          description: 'Once tenants start hitting the connector, rows appear here within a second.',
        }}
        onRowClick={(r) => r.traceId && onTraceOpen(r.traceId, r.tenantId)}
        columns={[
          {
            header: 'Tenant',
            cell: (r) => (
              <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {r.tenantId ?? '—'}
              </Typography>
            ),
            width: 140,
          },
          {
            header: 'Flow',
            cell: (r) => (
              <Box sx={{ py: 0.5 }}>
                <StageFlow tenant={r.tenant} platform={r.platform} abdm={r.abdm} compact />
              </Box>
            ),
            width: 600,
          },
          {
            header: 'API',
            cell: (r) => (
              <Stack spacing={0}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label={r.httpMethod ?? '?'}
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontFamily: 'JetBrains Mono, monospace',
                      bgcolor: theme.palette.brand.lighter,
                      color: theme.palette.brand.dark,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}
                  >
                    {r.path ?? '—'}
                  </Typography>
                </Stack>
                {r.apiCode && (
                  <Typography variant="caption" color="text.tertiary" sx={{ fontSize: 10 }}>
                    {r.apiCode}
                  </Typography>
                )}
              </Stack>
            ),
          },
          {
            header: 'HTTP',
            cell: (r) => (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'JetBrains Mono, monospace',
                  color:
                    (r.httpStatus ?? 0) >= 500 ? theme.palette.status.danger
                    : (r.httpStatus ?? 0) >= 400 ? theme.palette.status.warning
                    : theme.palette.status.success,
                  fontWeight: 600,
                }}
              >
                {r.httpStatus ?? '—'}
              </Typography>
            ),
            width: 70,
          },
          {
            header: 'Duration',
            cell: (r) => (
              <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {r.totalDurationMs != null ? `${r.totalDurationMs}ms` : '—'}
              </Typography>
            ),
            width: 90,
          },
          {
            header: 'When',
            cell: (r) => (
              <Typography variant="caption" color="text.secondary" title={utcStamp(r.createdAt)}>
                {relativeTime(r.createdAt)}
              </Typography>
            ),
            width: 130,
          },
        ]}
      />
      <Pagination page={data} onChange={onPage} />
    </>
  );
}

function EventsTab({
  query,
  onPage,
  onTraceOpen,
}: {
  query: AuditEventQuery;
  onPage: (n: number) => void;
  onTraceOpen: (traceId: string, tenantId: string) => void;
}) {
  const theme = useTheme();
  const swrKey = useMemo(() => ['events', query] as const, [query]);
  const { data, error, isLoading } = useSWR(swrKey, () => audit.events(query));

  return (
    <>
      <DataTable<AbdmEventLog>
        rows={data?.content}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        empty={{ icon: SearchIcon, title: 'No events match.', description: 'Loosen the filters or expand the time window.' }}
        onRowClick={(r) => r.traceId && onTraceOpen(r.traceId, r.tenantId)}
        columns={[
          {
            header: 'When',
            cell: (r) => <Typography variant="caption" color="text.secondary" title={utcStamp(r.createdAt)}>{relativeTime(r.createdAt)}</Typography>,
            width: 130,
          },
          {
            header: 'Tenant',
            cell: (r) => <Typography variant="caption">{redactMiddle(r.tenantId, 6, 6)}</Typography>,
            width: 160,
          },
          {
            header: 'Milestone',
            cell: (r) => <Typography variant="caption" sx={{ fontWeight: 600 }}>{r.milestone}</Typography>,
            width: 100,
          },
          {
            header: 'Event',
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.eventType}</Typography>
                {r.stepName && <Typography variant="caption" color="text.tertiary">{r.stepName}</Typography>}
              </Stack>
            ),
          },
          {
            header: 'Status',
            cell: (r) => <StatusPill tone={auditStatusTone(r.status)}>{r.status}</StatusPill>,
            width: 100,
          },
          {
            header: 'Message',
            cell: (r) => (
              <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {r.message ?? '—'}
              </Typography>
            ),
          },
          {
            header: 'Trace',
            cell: (r) => r.traceId ? (
              <Typography variant="caption" sx={{ fontFamily: theme.typography.fontFamily, color: theme.palette.brand.dark, fontWeight: 500 }}>
                {redactMiddle(r.traceId, 4, 4)}
              </Typography>
            ) : <Typography variant="caption" color="text.tertiary">—</Typography>,
            width: 100,
          },
        ]}
      />
      <Pagination page={data} onChange={onPage} />
    </>
  );
}

function TransactionsTab({
  query,
  onPage,
  onTraceOpen,
}: {
  query: AuditTransactionQuery;
  onPage: (n: number) => void;
  onTraceOpen: (traceId: string, tenantId: string) => void;
}) {
  const theme = useTheme();
  const swrKey = useMemo(() => ['transactions', query] as const, [query]);
  const { data, error, isLoading } = useSWR(swrKey, () => audit.transactions(query));

  return (
    <>
      <DataTable<AbdmTransactionLog>
        rows={data?.content}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        empty={{ icon: SearchIcon, title: 'No transactions match.', description: 'Loosen the filters or expand the time window.' }}
        onRowClick={(r) => r.traceId && onTraceOpen(r.traceId, r.tenantId)}
        columns={[
          {
            header: 'When',
            cell: (r) => <Typography variant="caption" color="text.secondary" title={utcStamp(r.createdAt)}>{relativeTime(r.createdAt)}</Typography>,
            width: 130,
          },
          {
            header: 'Tenant',
            cell: (r) => <Typography variant="caption">{redactMiddle(r.tenantId, 6, 6)}</Typography>,
            width: 160,
          },
          {
            header: 'API',
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.apiName}</Typography>
                <Typography variant="caption" color="text.tertiary">
                  {r.httpMethod ?? ''} {r.responseStatus ? `· ${r.responseStatus}` : ''}
                </Typography>
              </Stack>
            ),
          },
          {
            header: 'Status',
            cell: (r) => <StatusPill tone={auditStatusTone(r.status)}>{r.status}</StatusPill>,
            width: 110,
          },
          {
            header: 'Latency',
            align: 'right',
            cell: (r) => r.durationMs ? <Typography variant="caption">{r.durationMs}ms</Typography> : '—',
            width: 100,
          },
          {
            header: 'Trace',
            cell: (r) => r.traceId ? (
              <Typography variant="caption" sx={{ fontFamily: theme.typography.fontFamily, color: theme.palette.brand.dark, fontWeight: 500 }}>
                {redactMiddle(r.traceId, 4, 4)}
              </Typography>
            ) : '—',
            width: 100,
          },
        ]}
      />
      <Pagination page={data} onChange={onPage} />
    </>
  );
}

function TraceDrawer({ pick, onClose }: { pick: { traceId: string; tenantId?: string } | null; onClose: () => void }) {
  const theme = useTheme();
  const { data, error, isLoading } = useSWR<TraceTimeline>(
    pick ? ['trace', pick.traceId, pick.tenantId] : null,
    () => audit.trace(pick!.traceId, pick!.tenantId),
  );

  return (
    <Drawer
      anchor="right"
      open={!!pick}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', md: 540 } } }}
    >
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.border.subtle}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.tertiary">TRACE</Typography>
            <Typography variant="h2" sx={{ fontSize: 18, fontWeight: 600, fontFamily: theme.typography.fontFamily }}>
              {pick ? redactMiddle(pick.traceId, 6, 6) : ''}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} aria-label="close trace drawer">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
      <Box sx={{ p: 3, overflowY: 'auto' }}>
        {isLoading && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
        {error && (
          <Typography variant="body2" color="error">
            Couldn't load timeline: {(error as Error).message}
          </Typography>
        )}
        {data && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>
                Steps ({data.events.length})
              </Typography>
              <Stack spacing={0}>
                {data.events.length === 0 && <Typography variant="caption" color="text.tertiary">No event-log rows under this trace.</Typography>}
                {data.events.map((e) => {
                  const fix = e.status === 'FAIL' ? suggestResolution(e.message) : null;
                  return (
                    <Stack
                      key={e.id}
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                      sx={{ py: 1.25, borderBottom: `1px solid ${theme.palette.border.subtle}` }}
                    >
                      <StatusPill tone={auditStatusTone(e.status)} dot={false}>{e.status}</StatusPill>
                      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {e.milestone} · {e.eventType}
                        </Typography>
                        {e.stepName && (
                          <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            step: {e.stepName}
                          </Typography>
                        )}
                        {e.message && (
                          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{e.message}</Typography>
                        )}
                        <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {utcStamp(e.createdAt)} · {relativeTime(e.createdAt)}
                          {e.sourceIp && <> · ip: {e.sourceIp}</>}
                        </Typography>
                        {fix && (
                          <Typography variant="caption" sx={{ color: theme.palette.status.warning, fontStyle: 'italic', mt: 0.25 }}>
                            ↻ Suggested fix: {fix}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>
                Gateway calls ({data.transactions.length})
              </Typography>
              <Stack spacing={0}>
                {data.transactions.length === 0 && <Typography variant="caption" color="text.tertiary">No outbound gateway calls under this trace.</Typography>}
                {data.transactions.map((t) => {
                  const fix = t.status !== 'SUCCESS'
                    ? suggestResolution(t.errorMessage, `http=${t.responseStatus ?? ''}`, t.apiName)
                    : null;
                  return (
                    <Box key={t.id} sx={{ py: 1.25, borderBottom: `1px solid ${theme.palette.border.subtle}` }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500}>{t.apiName}</Typography>
                          <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {t.httpMethod ?? ''} {t.responseStatus ?? ''} · {t.durationMs ?? '—'}ms
                          </Typography>
                          {t.requestUrl && (
                            <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-all' }}>
                              URL: {t.requestUrl}
                            </Typography>
                          )}
                          {(t.requestId || t.abdmTransactionId) && (
                            <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {t.requestId && <>req: {t.requestId}{' '}</>}
                              {t.abdmTransactionId && <>· abdm-tx: {t.abdmTransactionId}</>}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {utcStamp(t.createdAt)} · {relativeTime(t.createdAt)}
                            {t.sourceIp && <> · ip: {t.sourceIp}</>}
                          </Typography>
                          {t.errorMessage && (
                            <Typography variant="caption" color="error.main" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {t.errorMessage}
                            </Typography>
                          )}
                          {fix && (
                            <Typography variant="caption" sx={{ color: theme.palette.status.warning, fontStyle: 'italic', mt: 0.25 }}>
                              ↻ Suggested fix: {fix}
                            </Typography>
                          )}
                        </Stack>
                        <StatusPill tone={auditStatusTone(t.status)} dot={false}>{t.status}</StatusPill>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>
                Trace metadata
              </Typography>
              <FieldRow label="Trace ID" value={data.traceId} mono />
              <FieldRow label="Tenant" value={data.events[0]?.tenantId ?? data.transactions[0]?.tenantId ?? '—'} mono />
            </Box>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

